import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';

interface Paciente {
  _id: string;
  nome: string;
  email: string;
}

interface AutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  readonly?: boolean;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Digite o nome",
  className = "",
  required = false,
  readonly = false
}) => {
  const [suggestions, setSuggestions] = useState<Paciente[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState<Paciente | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (readonly && value) {
      // Em modo readonly, criar um paciente "fake" com o nome fornecido
      if (!selectedPaciente || selectedPaciente.nome !== value) {
        setSelectedPaciente({ _id: 'readonly', nome: value, email: '' });
      }
      return;
    }

    // Se não está em readonly e não há valor, limpar seleção
    if (!readonly && !value && selectedPaciente) {
      setSelectedPaciente(null);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (value.length > 0 && !selectedPaciente && !readonly) {
      timeoutRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const results = await apiService.searchPacientes(value);
          setSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Erro ao buscar pacientes:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    } else if (!value) {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, readonly]); // Removido selectedPaciente das dependências

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedPaciente) {
      onChange(e.target.value);
    }
  };

  const handleSuggestionClick = (paciente: Paciente) => {
    setSelectedPaciente(paciente);
    onChange(paciente.nome);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleClearSelection = () => {
    if (readonly) return; // Não permitir limpar em modo readonly
    
    setSelectedPaciente(null);
    onChange('');
    setShowSuggestions(false);
    setSuggestions([]);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputFocus = () => {
    if (value.length > 0 && suggestions.length > 0 && !selectedPaciente) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 150);
  };

  return (
    <div className="position-relative">
      {selectedPaciente ? (
        <div className="form-control d-flex align-items-center justify-content-between" style={{ minHeight: '38px' }}>
          <span className="badge bg-success d-flex align-items-center gap-2 px-3 py-2" style={{ fontSize: '1rem' }}>
            <span>{selectedPaciente.nome.toUpperCase()}</span>
            {!readonly && (
              <button
                type="button"
                className="btn-close btn-close-white"
                style={{ fontSize: '0.7rem' }}
                onClick={handleClearSelection}
                aria-label="Remover paciente"
              />
            )}
          </span>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="text"
            className={`form-control ${className}`}
            value={value}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            required={required}
            autoComplete="off"
          />
          
          {showSuggestions && (suggestions.length > 0 || loading) && (
            <div className="position-absolute w-100 bg-white border rounded shadow-sm" style={{ zIndex: 1000, top: '100%' }}>
              {loading ? (
                <div className="p-2 text-center">
                  <output className="spinner-border spinner-border-sm">
                    <span className="visually-hidden">Carregando...</span>
                  </output>
                </div>
              ) : (
                suggestions.map((paciente) => (
                  <button
                    key={paciente._id}
                    type="button"
                    className="w-100 p-2 border-0 border-bottom text-start"
                    style={{ cursor: 'pointer', backgroundColor: 'white' }}
                    onClick={() => handleSuggestionClick(paciente)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div className="fw-medium">{paciente.nome.toUpperCase()}</div>
                    <small className="text-muted">{paciente.email}</small>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Autocomplete;